/* Rechterkolom van de Unified Inbox — header, live-banner, kanaal-filter,
 * berichtenlijst en composer. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Send, Sparkles, Loader2, Paperclip } from "lucide-react";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import { TAGS, tagMeta, channelMeta, FILTER_CHANNELS } from "./constants";
import MessageBubble from "./MessageBubble";

export default function ConversationPanel({ pillar, thread, loading, channelsConnected, onSetTag, onSendReply, onApprove, onReject, onSaveEdit, onTranslate, onRegenerate, onSend, onSendNow, onSubmit }) {
    const prospect = thread.prospect;
    const messages = thread.messages || [];
    const [reply, setReply] = useState("");
    const [channel, setChannel] = useState(channelsConnected[0] || "linkedin");
    // Channel filter — multiselect. `null` (default) means "show all".
    // The pill row ALWAYS renders every FILTER_CHANNELS entry (with count,
    // even at 0) so the user has a stable view of which channels exist.
    const [channelFilter, setChannelFilter] = useState(null);

    // Count messages per channel — populated for every FILTER_CHANNELS entry
    // so pills always render (0 counts included). Other channels observed in
    // the thread but not in the canonical list still appear.
    const channelCounts = useMemo(() => {
        const acc = {};
        for (const c of FILTER_CHANNELS) acc[c] = 0;
        for (const m of messages) {
            const key = m.channel || "unknown";
            acc[key] = (acc[key] || 0) + 1;
        }
        return acc;
    }, [messages]);
    const visibleMessages = (channelFilter && channelFilter.length > 0)
        ? messages.filter((m) => channelFilter.includes(m.channel))
        : messages;
    const toggleChannelFilter = (id) => {
        setChannelFilter((prev) => {
            if (prev === null) return [id]; // start selection
            const isOn = prev.includes(id);
            const next = isOn ? prev.filter((x) => x !== id) : [...prev, id];
            return next.length === 0 ? null : next;   // empty selection → "all"
        });
    };
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef();

    useEffect(() => {
        // Auto-scroll to bottom on new messages
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.length, thread.prospect.id]);

    const generate = async () => {
        setGenerating(true);
        const firstName = (prospect.name || "").split(" ")[0] || "there";
        const greet = `Hi ${firstName},\n\n`;
        // Sensible fallback so the textarea is never left blank even when
        // /tone/preview returns nothing (e.g. workspace has no tone configured).
        const fallback = i18n.t("inbox.fallback_draft");
        try {
            const r = await api.post("/tone/preview", {
                master_type: pillar === "recruit" ? "recruit" : "sales",
                scenario: "first_touch",
            });
            const body = (r.data?.preview || "").trim() || fallback;
            setReply(greet + body);
            toast.success(i18n.t("inbox.draft_ok"));
        } catch {
            setReply(greet + fallback);
            toast.success(i18n.t("inbox.draft_fallback"));
        } finally { setGenerating(false); }
    };

    const send = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await onSendReply(channel, reply, channel === "email" ? "Vervolg" : null);
            setReply("");
        } finally { setSending(false); }
    };

    const tm = tagMeta(prospect.inbox_tag);
    const lastReplied = useMemo(() => messages.slice().reverse().find((m) => m.direction === "in"), [messages]);
    const lastOutAgent = useMemo(() => messages.slice().reverse().find((m) => m.direction === "out" && m.status === "sent"), [messages]);

    return (
        <>
            {/* Header */}
            <header className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--jr-surface-2)] grid place-items-center text-[11px] font-mono uppercase text-[var(--jr-text-2)] shrink-0">
                    {(prospect.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white truncate">{prospect.name || "Unknown"}</h3>
                        <LinkedInButton url={prospect.linkedin_url} name={prospect.name} variant="inline" className="text-xs" testid="conv-linkedin-link" />
                    </div>
                    <p className="text-xs text-[var(--jr-text-2)] truncate">
                        {prospect.title} {prospect.company_name && `· ${prospect.company_name}`}
                        {prospect.campaign && <span className="ml-2 text-[var(--jr-text-3)]">[{prospect.campaign}]</span>}
                    </p>
                </div>
                <label className="flex items-center gap-2" data-testid="conv-tag-picker">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Tag</span>
                    <select value={prospect.inbox_tag} onChange={(e) => onSetTag(e.target.value)}
                        className={`text-xs font-mono uppercase tracking-[0.14em] px-2 py-1 rounded border outline-none cursor-pointer ${tm.cls}`}>
                        {TAGS.map((t) => <option key={t.id} value={t.id} className="bg-[var(--jr-surface)] text-white normal-case">{t.label}</option>)}
                    </select>
                </label>
            </header>

            {/* Live banner */}
            {lastReplied && (
                <div className="mx-6 mt-4 rounded-lg border border-[#22c55e]/30 bg-[#22c55e]/[0.06] px-4 py-2 flex items-center gap-3"
                    data-testid="live-banner">
                    <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
                    <span className="text-xs text-[var(--jr-text-2)]">
                        <span className="text-[#22c55e] font-medium">Live</span>
                        {lastOutAgent?.ai_generated && <span className="ml-2">Agent replied</span>}
                    </span>
                    <button onClick={() => onSetTag("interested")}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 text-[10px] font-mono uppercase tracking-[0.16em]"
                        data-testid="conv-mark-interested">
                        <Sparkles size={10} /> {i18n.t("inbox.mark_interested")}
                    </button>
                </div>
            )}

            {/* Channel filter pills — multiselect.
             *  Renders ALWAYS the 3 canonical channels (LinkedIn, Email,
             *  WhatsApp) + LinkedIn Connect if present in the thread, even
             *  if their count is 0. User can toggle any combination.
             */}
            <div className="mx-6 mt-4 flex items-center gap-1.5 flex-wrap" data-testid="channel-filter-bar">
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mr-1">{i18n.t("inbox.channel_lbl")}</span>
                <button onClick={() => setChannelFilter(null)} data-testid="channel-filter-all"
                    className={`text-[11px] px-2.5 py-0.5 rounded-full border ${channelFilter === null ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)]" : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                    {i18n.t("common.all")} · {messages.length}
                </button>
                {/* Canonical channels: ALWAYS visible even at count=0 */}
                {FILTER_CHANNELS.map((id) => {
                    const cm = channelMeta(id);
                    const CIcon = cm.Icon;
                    const active = channelFilter?.includes(id);
                    const count = channelCounts[id] || 0;
                    return (
                        <button key={id} onClick={() => toggleChannelFilter(id)}
                            data-testid={`channel-filter-${id}`}
                            className={`text-[11px] px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1.5 ${active ? `${cm.bg} ${cm.fg} ${cm.border}` : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"} ${count === 0 ? "opacity-60" : ""}`}>
                            <CIcon size={10} /> {cm.short} · {count}
                        </button>
                    );
                })}
                {/* Any other channel present in the thread that isn't in the canonical list */}
                {Object.entries(channelCounts).filter(([id]) => !FILTER_CHANNELS.includes(id)).map(([id, n]) => {
                    if (n === 0) return null;
                    const cm = channelMeta(id);
                    const CIcon = cm.Icon;
                    const active = channelFilter?.includes(id);
                    return (
                        <button key={id} onClick={() => toggleChannelFilter(id)}
                            data-testid={`channel-filter-${id}`}
                            className={`text-[11px] px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1.5 ${active ? `${cm.bg} ${cm.fg} ${cm.border}` : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                            <CIcon size={10} /> {cm.short} · {n}
                        </button>
                    );
                })}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto min-w-0 px-6 py-4 space-y-3" data-testid="conv-messages">
                {loading ? (
                    <div className="text-center text-xs text-[var(--jr-text-3)]">{i18n.t("common.loading")}</div>
                ) : visibleMessages.length === 0 ? (
                    <div className="text-center text-xs text-[var(--jr-text-3)]">
                        {channelFilter && channelFilter.length > 0
                            ? i18n.t("inbox.no_channel_msgs", { channels: channelFilter.map((c) => channelMeta(c).label).join(" / ") })
                            : i18n.t("inbox.no_msgs")}
                    </div>
                ) : (
                    visibleMessages.map((m) => (
                        <MessageBubble key={m.id} msg={m} prospect={prospect}
                            onApprove={onApprove} onReject={onReject}
                            onSaveEdit={onSaveEdit} onTranslate={onTranslate} onRegenerate={onRegenerate}
                            onSend={onSend} onSendNow={onSendNow} onSubmit={onSubmit} />
                    ))
                )}
            </div>

            {/* Composer */}
            <footer className="border-t border-white/8 p-4">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                    rows={2}
                    placeholder={i18n.t("inbox.write_msg")}
                    data-testid="conv-composer"
                    className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)] resize-none" />
                <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button type="button" className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/5" title={i18n.t("inbox.attachment_soon")}>
                            <Paperclip size={14} />
                        </button>
                        <button onClick={generate} disabled={generating} data-testid="conv-ai-draft"
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs text-[#a78bfa] hover:bg-[#a78bfa]/10 disabled:opacity-50">
                            {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Draft
                        </button>
                        <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="conv-channel"
                            className="text-[10px] font-mono uppercase tracking-[0.16em] bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1 text-[var(--jr-text-2)] outline-none">
                            <option value="linkedin">LinkedIn</option>
                            <option value="email">Email</option>
                            <option value="whatsapp">WhatsApp</option>
                        </select>
                    </div>
                    <button onClick={send} disabled={!reply.trim() || sending} data-testid="conv-send"
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {i18n.t("common.send")}
                    </button>
                </div>
            </footer>
        </>
    );
}
